import React, {type ReactNode, useEffect, useState} from 'react';

import {AutoHeightTitleInput} from 'app/components/general/AutoHeightTitleInput';
import {RichTextarea} from 'app/components/general/RichTextarea';
import {DocHeaderStructure} from 'app/general/structures/DocHeader';
import {DocumentMetadataStatusButtons} from 'app/general/structures/DocumentMetadataStatusButtons';
import {DocumentThumbnail} from 'app/general/structures/DocumentThumbnail';
import StateButton from 'app/general/widgets/StateButton';
import {ImageUploaderForDocuments} from 'app/general/widgets/Uploaders/ImageUploaderForDocuments';
import {DocumentPolicyList} from 'app/policies/components/DocumentPolicyList';
import {EditableStepEmbedsGallery} from 'app/procedures/components/ProcedureEditPage/EditableStepEmbedsGallery';
import {EditableStepImagesGallery} from 'app/procedures/components/ProcedureEditPage/EditableStepImagesGallery';
import {StepEmbedOptions} from 'app/procedures/components/ProcedureEditPage/StepEmbedOptions';
import {StepVideosTitle} from 'app/procedures/components/structures';
import {useAccountSettingsState} from 'app/settings/api';
import {DocumentTagsList} from 'app/tags/components/DocumentTagsList';
import {EditableTagsSelect} from 'app/tags/components/EditableTagsSelect';
import {gt} from 'app/utils/i18n';
import {cdnImageSrc} from 'app/utils/strings';

import type {VersionDiffType} from 'app/components/VersionDiffer';
import type {
  AttachedMedia,
  ProcedureHashidSerializedType,
  ProcedureSerializedType,
} from 'sweet-types';

type Props = {
  content: Pick<VersionDiffType, 'name' | 'description'>;
  isBusy: boolean;
  updateContent: (
    content: Partial<
      Pick<VersionDiffType, 'name' | 'description' | 'thumbnail'>
    >
  ) => unknown;
  procedure: ProcedureSerializedType | ProcedureHashidSerializedType;
  warningComponent: ReactNode;
  setEditing: (editingMeta: boolean) => void;
  canEditTags: boolean;
  canCreateTags: boolean;
  onTagsChange: (action: Promise<any>) => Promise<any>;
  setReviewDate: (value: string | null) => Promise<any>;
  onChangeBusyStatus: (id: string, busy: boolean) => void;
  contentType: string;
  objectId: number;
  initialThumbnail: string | null;
};

export function EditProcedureMetaData({
  content,
  onChangeBusyStatus,
  setEditing,
  updateContent,
  procedure,
  canEditTags,
  canCreateTags,
  warningComponent,
  contentType,
  objectId,
  onTagsChange,
  initialThumbnail,
}: Props): JSX.Element {
  const {MEDIA_URL_READ} = useAccountSettingsState().data!;

  const [name, setName] = useState<VersionDiffType['name']>(content.name);
  const [description, setDescription] = useState<
    VersionDiffType['description']
  >(content.description);

  useEffect(() => {
    return () => onChangeBusyStatus('meta', false);
  }, []);

  useEffect(() => {
    setName(content.name);
  }, [content.name]);

  useEffect(() => {
    setDescription(content.description);
  }, [content.description]);

  const handleDescriptionContentChange = (newContent: string) => {
    if (description.content === newContent) {
      return;
    }

    const newDescription = {...(description ?? {}), content: newContent};
    updateContent({
      description: newDescription,
    });

    setDescription(newDescription);
  };

  const handleNameChange = (newName: string) => {
    if (name === newName) {
      return;
    }
    updateContent({
      name: newName,
    });
    setName(newName);
  };

  const handleDone = () => setEditing(false);

  const addImage = (image: AttachedMedia) => {
    const newDescription = {
      ...description,
      images: [...description.images, image],
    };
    updateContent({
      description: newDescription,
    });
    setDescription(newDescription);
  };

  const addIframe = (iframe: string) => {
    const newDescription = {
      ...description,
      embeds: [...(description.embeds ?? []), {embed_html: iframe}],
    };
    updateContent({
      description: newDescription,
    });
    setDescription(newDescription);
  };

  const remove = (index: number, field: 'images' | 'embeds') => {
    const newDescription = {
      ...description,
      [field]: description[field].filter((item, i) => i != index),
    };
    updateContent({
      description: newDescription,
    });
    setDescription(newDescription);
  };

  function onEditImage(newImageRef: string, index: number) {
    const newDescription = {
      ...description,
      images: description.images.map((img, i) =>
        i === index ? {...img, image_ref: newImageRef} : img
      ),
    };
    updateContent({
      description: newDescription,
    });
    setDescription(newDescription);
  }

  const handleEditorIsBusyChange = (isBusy: boolean) => {
    onChangeBusyStatus('meta', isBusy);
  };

  return (
    <DocHeaderStructure
      advancedContent={
        <div className="row">
          <div className="col-12 text-muted mb-2 text-right">
            <DocumentMetadataStatusButtons
              canEdit={procedure.can_edit}
              context={'edit'}
              contentType="procedure"
              document={procedure}
            />
          </div>
        </div>
      }
      warningContent={warningComponent}
      titleContent={
        <div className="d-flex align-items-center">
          <DocumentThumbnail
            initialThumbnail={initialThumbnail}
            onUpdateThumbnail={(thumbnail: string) =>
              updateContent({thumbnail})
            }
          />
          <h1 className="flex-grow-1">
            <AutoHeightTitleInput
              className="form-group"
              maxLength={128}
              placeholder={gt('Procedure Title')}
              onChange={handleNameChange}
              value={name}
            />
          </h1>
        </div>
      }
      tagsContent={
        canEditTags ? (
          <div className="form-group">
            <EditableTagsSelect
              canCreateTags={canCreateTags}
              documents={[
                {
                  object_id: procedure.id,
                  document_content_type: 'procedure',
                  tags: procedure.tags,
                },
              ]}
              selected={procedure.tags}
              onChange={onTagsChange}
            />
          </div>
        ) : (
          <DocumentTagsList showNoEditMessage={true} tags={procedure.tags} />
        )
      }
      policyContent={<DocumentPolicyList policies={procedure.policies} />}
      descriptionContent={
        <div className="form-group">
          <RichTextarea
            onEditorIsBusyChange={handleEditorIsBusyChange}
            value={cdnImageSrc(description.content, MEDIA_URL_READ, 'original')}
            onChange={handleDescriptionContentChange}
            contentType={contentType}
            objectId={objectId}
            onCloseRequest={handleDone}
          />
        </div>
      }
      extraContent={
        <div>
          {(description.images && description.images.length > 0) ||
          (description.embeds && description.embeds.length > 0) ? (
            <StepVideosTitle className="mt-3" />
          ) : null}
          <div className="step-image-edit clearfix images">
            <EditableStepImagesGallery
              images={description.images ?? []}
              onRemoveImage={(img, index) => remove(index, 'images')}
              onEditImage={onEditImage}
            />
            <EditableStepEmbedsGallery
              embeds={description.embeds ?? []}
              onRemove={(img, index) => remove(index, 'embeds')}
            />
          </div>
          <ImageUploaderForDocuments onAddImage={addImage} />

          <div className="more-tools clearfix">
            <StepEmbedOptions
              onAddVideo={addImage}
              onAddIframe={addIframe}
              hideFileUpload={true}
              hideAddField={true}
              onBusy={(id: number, isBusy: boolean) =>
                handleEditorIsBusyChange(isBusy)
              }
            />

            <StateButton
              className="btn-primary float-right"
              busyText={gt('Saving...')}
              onClick={handleDone}
            >
              <i className="fa fa-thumbs-up" /> {gt('Finished Editing')}
            </StateButton>
          </div>
        </div>
      }
    />
  );
}
